// @ts-check

import { useQuery, useMutation } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { isEmpty } from "lodash";
import {
  getOrgConfigForTenant as getOrgConfigForTenantQuery,
  updateOrgConfigForTenant as updateOrgConfigForTenantMutation,
} from "src/queries/orgConfig.graphql";
import { omitTypename } from "src/util/graphql";

/**
 * @typedef {import("@apollo/client").ApolloError} ApolloError
 */

/**
 * Hook for managing org config for tenant.
 * @returns {{
 *  loading: boolean,
 *  config: org_config.tenant.Config,
 *  createdBy: string,
 *  createdAt: import("moment").Moment,
 *  update: function(org_config.tenant.Config): Promise<boolean>
 *  updating: boolean,
 * }}
 */
export const useOrgConfigForTenant = () => {
  const { data, loading } = useQuery(getOrgConfigForTenantQuery, {
    fetchPolicy: "cache-first", // all config updates will update cache so can avoid spamming the network
  });

  const [updateConfig, { loading: updating }] = useMutation(
    updateOrgConfigForTenantMutation
  );

  const config = useMemo(() => omitTypename(data?.config), [data]);

  const update = useCallback(
    /**
     * Partial update
     * @param {org_config.tenant.Config} newConfig
     * @returns {Promise<boolean>}
     */
    async (newConfig) => {
      if (loading) {
        return false;
      }

      const { errors } = await updateConfig({
        variables: { input: { config: newConfig } },
        update: (cache, { data: updateData, errors: updateErrors }) => {
          if (!isEmpty(updateErrors) || !updateData?.config) {
            return;
          }

          const existingData = cache.readQuery({
            query: getOrgConfigForTenantQuery,
          });

          if (existingData) {
            cache.writeQuery({
              query: getOrgConfigForTenantQuery,
              data: {
                config: updateData.config,
              },
            });
          }
        },
      });

      if (!isEmpty(errors)) {
        return false;
      }

      return true;
    },
    [updateConfig, loading]
  );

  return {
    loading,
    config: config?.config,
    createdBy: config?.createdBy,
    createdAt: config?.createdAt,
    update,
    updating,
  };
};
